import { DOCUMENT } from "@angular/common";
import { Inject, Injectable } from "@angular/core";
import { RoomEntity, RoomId, StreamId } from "@common/domain";
import {
  Actions,
  concatLatestFrom,
  createEffect,
  EffectNotification,
  ofType,
  OnRunEffects
} from "@ngrx/effects";
import { getSelectors, RouterReducerState } from "@ngrx/router-store";
import { createFeatureSelector, Store } from "@ngrx/store";
import {
  ConferenceStreamsListService,
  LocalConferenceStreamActions,
  RemoteConferenceStreamActions
} from "@youm-front/conference-stream/data-access";
import { YoumAPIFacade } from "@youm-front/core";
import {
  RoomError,
  RoomJoinCmd,
  RoomStreamAudioUpdateCmd,
  RoomStreamToSubscribeVO
} from "@youm-front/room/domain";
import { ROOM_APP_URL } from "@youm-front/room/provider";
import { ErrorHandlerService } from "@youm-front/utils/error-handler";
import {
  defer,
  EMPTY,
  fromEvent,
  merge,
  Observable,
  of,
  Subject,
  timer
} from "rxjs";
import {
  catchError,
  concatMap,
  exhaustMap,
  filter,
  finalize,
  map,
  mergeMap,
  repeat,
  switchMap,
  take,
  takeUntil,
  tap
} from "rxjs/operators";
import { AnyConferenceStream } from "youm-lib/conference";
import { RoomActions, RoomStreamActions } from "../actions";
import { RoomStreamsListRequestDTO } from "../dto";
import { RoomEvents, RoomStreamEvents } from "../events";
import { RoomContextFacade } from "../facades";
import { reconcileRoomRemoteStreams } from "../reducers";
import { RoomRemoteStreamsSelectors, RoomSelectors } from "../selectors";
import { RoomService } from "../services";
import { RoomStreamsReconcileCadence } from "./room-streams-reconcile-cadence";

const selectRouter = createFeatureSelector<RouterReducerState>("router");
const { selectRouteParams } = getSelectors(selectRouter);

/**
 * @deprecated
 * This effect enables too much logic - room, streams and producer.
 */
@Injectable()
export class RoomEffects implements OnRunEffects {
  constructor(
    @Inject(DOCUMENT) private document: Document,
    private readonly errorHandlerService: ErrorHandlerService,
    @Inject(ROOM_APP_URL) readonly roomAppUrl: string,
    private readonly _actions$: Actions,
    private readonly _roomService: RoomService,
    private readonly conferenceStreamsListService: ConferenceStreamsListService,
    private readonly _roomContextFacade: RoomContextFacade,
    private readonly _youmAPIFacade: YoumAPIFacade,
    private readonly _store: Store
  ) {}

  init$ = createEffect(() =>
    this._youmAPIFacade.connected$.pipe(
      filter((_) => _),
      concatLatestFrom(() => [this._store.select(selectRouteParams)]),
      mergeMap(([, routeParams]) => [
        RoomActions.reset(),
        RoomActions.loadRoomData({ roomId: routeParams["roomId"] })
      ])
    )
  );

  disposeRoomData$ = createEffect(() =>
    this._actions$.pipe(
      ofType(RoomActions.init, RoomActions.destroy, RoomActions.reset),
      mergeMap(() => [
        RoomActions.disposeRoomLocalData(),
        RoomActions.disposeRoomRemoteData()
      ])
    )
  );

  disposeRoomLocalData$ = createEffect(() =>
    this._actions$.pipe(
      ofType(RoomActions.disposeRoomLocalData),
      map(() => LocalConferenceStreamActions.dispose())
    )
  );

  disposeRoomRemoteData$ = createEffect(() =>
    this._actions$.pipe(
      ofType(RoomActions.disposeRoomRemoteData),
      map(() => RemoteConferenceStreamActions.dispose())
    )
  );

  loadRoomData$ = createEffect(() =>
    this._actions$.pipe(
      ofType(RoomActions.loadRoomData),
      switchMap((_) =>
        this._roomService.roomGet(_.roomId).pipe(
          mergeMap((_: RoomEntity) => [
            RoomActions.loadRoomDataSuccess({ room: _ }),
            RoomActions.joinRoom({
              roomId: _.value.pid,
              roomContextId: this._roomContextFacade.roomContextId
            })
          ]),
          catchError((_: Error) => [
            RoomActions.loadRoomDataFailure({ error: _.message })
          ])
        )
      )
    )
  );

  roomJoin$ = createEffect(() =>
    this._actions$.pipe(
      ofType(RoomActions.joinRoom),
      concatLatestFrom(() => [
        this._store
          .select(RoomSelectors.room)
          .pipe(mergeMap((_) => (_ ? [_] : EMPTY)))
      ]),
      switchMap(([_, room]) =>
        this._roomService
          .roomJoin(
            new RoomJoinCmd({
              roomContextId: _.roomContextId,
              roomId: _.roomId
            })
          )
          .pipe(
            mergeMap(() => [
              RoomActions.joinRoomSuccess(),
              RoomActions.publishWebcamStream(),
              // List room streams only after success join
              RoomActions.listRoomStreams({ roomId: room.value.id })
            ]),
            catchError((_) => [RoomActions.joinRoomFailure({ error: _ })])
          )
      )
    )
  );

  roomJoinFailure$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(RoomActions.joinRoomFailure),
        tap((_) => {
          if (_.error.code === RoomError.NOT_FOUND) {
            this.document.location.href = this.roomAppUrl;
            return;
          }

          this.errorHandlerService.error(_);
        })
      ),
    { dispatch: false }
  );

  listRoomStreams$ = createEffect(() =>
    this._actions$.pipe(
      ofType(RoomActions.listRoomStreams),
      switchMap((_) =>
        this._roomService
          .listRoomStreams(new RoomStreamsListRequestDTO({ roomId: _.roomId }))
          .pipe(
            switchMap((_) => [
              RoomActions.listRoomStreamsSuccess({
                streams: _.data.streams.map(
                  (_) => new RoomStreamToSubscribeVO(_)
                )
              })
            ]),
            catchError((error: Error) => [
              RoomActions.listRoomStreamsFailure({
                error: error.message
              })
            ])
          )
      )
    )
  );

  listRoomStreamsSuccessListenRemoteStreams$ = createEffect(() =>
    this._actions$.pipe(
      ofType(RoomActions.listRoomStreamsSuccess),
      switchMap(({ streams }) =>
        streams
          // Ignore room stream with the same roomContextId,
          // the local instance should be used
          .filter(
            (_) =>
              _.value.roomContextId !== this._roomContextFacade.roomContextId
          )
          .map((roomStreamToSubscribeVO) =>
            RoomEvents.remoteRoomStream({
              roomStreamToSubscribeVO
            })
          )
      )
    )
  );

  // A missed room stream event leaves a remote stream missing from the room
  // (or a deleted one shown), and a subscribe whose retries were exhausted
  // leaves it without media, until the next reset (YOUM-244). While the room
  // is joined, converge both the remote room streams (store) and their
  // subscriptions (media) to the stream list the server returns for this
  // client: every 30 s, every 5 s for a bounded time after a join or rejoin
  // or a sign of drift (see RoomStreamsReconcileCadence), and at once when
  // the tab becomes visible again or a subscribe failed for good. One pass at
  // a time; stopped on reset, leave and socket disconnect (whose reconnect
  // resets the room, joins and lists its streams again).
  reconcileRoomStreams$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        RoomActions.joinRoomSuccess,
        RoomActions.joinRoomFailure,
        RoomActions.reset,
        RoomActions.destroy,
        RoomActions.disposeRoomRemoteData
      ),
      concatLatestFrom(() => [this._store.select(RoomSelectors.room)]),
      switchMap(([action, room]) =>
        action.type === RoomActions.joinRoomSuccess.type && room
          ? this.roomStreamsReconcileLoop(room.value.id)
          : EMPTY
      )
    )
  );

  toggleStreamSound$ = createEffect(() =>
    this._actions$.pipe(
      ofType(RoomActions.toggleStreamSound),
      mergeMap(
        ({
          stream: {
            value: { streamId, roomId }
          }
        }) =>
          this.conferenceStreamsListService.cStreams$.pipe(
            take(1),
            map((_) => _.find((_) => _.streamId === streamId) || null),
            filter((_): _ is AnyConferenceStream => !!_),
            mergeMap((stream) =>
              stream.isAudioEnabled$.pipe(
                take(1),
                mergeMap((isAudioEnabled) =>
                  isAudioEnabled
                    ? [
                        RoomStreamActions.roomStreamAudioUpdate({
                          roomStreamAudioUpdateCmd:
                            new RoomStreamAudioUpdateCmd({
                              roomId,
                              streamId,
                              isEnabled: false
                            })
                        })
                      ]
                    : [
                        RoomStreamActions.roomStreamAudioUpdate({
                          roomStreamAudioUpdateCmd:
                            new RoomStreamAudioUpdateCmd({
                              roomId,
                              streamId,
                              isEnabled: true
                            })
                        })
                      ]
                )
              )
            )
          )
      )
    )
  );

  roomStreamRestart$ = createEffect(() =>
    this._actions$.pipe(
      ofType(RoomStreamActions.roomStreamRestart),
      mergeMap((_) =>
        this._roomService.roomStreamRestart(_.roomStreamRestartCmd).pipe(
          mergeMap(() => [
            RoomStreamActions.roomStreamRestartSuccess({
              roomStreamRestartCmd: _.roomStreamRestartCmd
            })
          ]),
          catchError((_) => [
            RoomStreamActions.roomStreamRestartFailure({
              error: _.message
            })
          ])
        )
      )
    )
  );

  roomStreamAudioUpdate$ = createEffect(() =>
    this._actions$.pipe(
      ofType(RoomStreamActions.roomStreamAudioUpdate),
      mergeMap((_) =>
        this._roomService
          .roomStreamAudioUpdate(_.roomStreamAudioUpdateCmd)
          .pipe(
            mergeMap(() => [
              RoomStreamActions.roomStreamAudioUpdateSuccess({
                roomStreamAudioUpdateCmd: _.roomStreamAudioUpdateCmd
              })
            ]),
            catchError((_) => [
              RoomStreamActions.roomStreamAudioUpdateFailure({
                error: _.message
              })
            ])
          )
      )
    )
  );

  private roomStreamsReconcileLoop(roomId: RoomId) {
    return defer(() => {
      const cadence = new RoomStreamsReconcileCadence();
      // Joined or rejoined: streams may be republished after the join list.
      cadence.recover();
      const wake$ = new Subject<void>();
      const signals = merge(
        fromEvent(this.document, "visibilitychange").pipe(
          filter(() => this.document.visibilityState === "visible"),
          tap(() => cadence.requestPass())
        ),
        this._actions$.pipe(
          ofType(RemoteConferenceStreamActions.streamSubscribeFailure),
          // Ignored while cooling down: waking would restart the delay.
          filter(() => cadence.outOfSync(true))
        )
      ).subscribe(() => wake$.next());

      // Wait, one pass, and again: passes never overlap.
      return merge(of(null), wake$).pipe(
        switchMap(() => timer(cadence.nextDelayMs())),
        take(1),
        concatMap(() => this.roomStreamsReconcile(roomId, cadence)),
        repeat(),
        finalize(() => signals.unsubscribe())
      );
    });
  }

  private roomStreamsReconcile(
    roomId: RoomId,
    cadence: RoomStreamsReconcileCadence
  ) {
    return defer(() => {
      cadence.passStarted();
      // Clean: the list succeeded and the store already matched it.
      let clean = false;
      const requestedAt = performance.now();
      const roomContextId = this._roomContextFacade.roomContextId;
      // Streams with a room stream event while the list is in flight: the
      // event may be newer than the list, the store keeps it as is.
      const skipStreamIds = new Set<StreamId>();
      const roomStreamEvents = this.roomStreamEventIds()
        .pipe(tap((_) => skipStreamIds.add(_)))
        .subscribe();

      return this._roomService
        .listRoomStreams(new RoomStreamsListRequestDTO({ roomId }))
        .pipe(
          take(1),
          concatLatestFrom(() =>
            this._store.select(
              RoomRemoteStreamsSelectors.roomRemoteStreamsState
            )
          ),
          mergeMap(([_, remoteStreams]) => {
            const streams = _.data.streams
              .map((_) => new RoomStreamToSubscribeVO(_).value)
              // The local instance is used for a stream of this context.
              .filter((_) => _.roomContextId !== roomContextId);

            clean =
              reconcileRoomRemoteStreams(remoteStreams, streams, [
                ...skipStreamIds
              ]) === remoteStreams;

            return [
              RoomEvents.remoteRoomStreamsReconcile({
                streams,
                skipStreamIds: [...skipStreamIds]
              }),
              // The stream manager tracks its own add/delete times.
              RemoteConferenceStreamActions.reconcile({
                streams: streams.map((_) => ({
                  id: _.streamId,
                  kinds: _.streamKinds,
                  credentials: _.credentials,
                  roomContextId
                })),
                requestedAt
              })
            ];
          }),
          // Best effort: the next pass tries again, sooner.
          catchError(() => EMPTY),
          // Before the completion reaches the loop, which then reads the
          // cadence for the next delay.
          tap({ complete: () => cadence.passDone(clean) }),
          finalize(() => roomStreamEvents.unsubscribe())
        );
    });
  }

  private roomStreamEventIds(): Observable<StreamId> {
    return merge(
      this._actions$.pipe(
        ofType(RoomEvents.remoteRoomStream),
        map((_) => _.roomStreamToSubscribeVO.value.streamId)
      ),
      this._actions$.pipe(
        ofType(RoomEvents.roomStreamUpdate),
        map((_) => _.roomStreamUpdateEventVO.value.streamId)
      ),
      this._actions$.pipe(
        ofType(
          RoomStreamEvents.roomStreamDeleteEvent,
          RoomStreamActions.roomStreamDeleteSuccess
        ),
        map((_) => _.roomStreamDeleteCmd.value.streamId)
      )
    );
  }

  ngrxOnRunEffects(resolvedEffects$: Observable<EffectNotification>) {
    return this._actions$.pipe(
      ofType(RoomActions.init),
      exhaustMap(() =>
        resolvedEffects$.pipe(
          takeUntil(this._actions$.pipe(ofType(RoomActions.destroy)))
        )
      )
    );
  }
}
